/**
 * src/components/ChatModal.tsx
 *
 * File-level:
 * - Chat modal component showing conversation between current user and another user.
 * - Improvements:
 *   - Robust timestamp parsing and normalization to avoid incorrect ordering.
 *   - Stable sorting using numeric timestamps plus tie-breaker to avoid mixups.
 *   - Preserves existing handshake (sider-chat-loaded) to avoid premature mark-read.
 *
 * Notes:
 * - This component expects the `useChat` hook from '@/hooks/useChat' which provides SSE messages.
 * - Incoming payloads can use a variety of timestamp and message field names; the component
 *   normalizes them so UI sorting and display are consistent.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import useChat, { ChatMessage } from '@/hooks/useChat';
import useAuthStore from '@/stores/useAuthStore';
import { Button } from '@/components/ui/button';
import { X, Send, MessageCircle, Check } from 'lucide-react';
import { trackModalView } from '@/utils/visitorTracking';

/**
 * ChatModalProps
 * @description Props for ChatModal component.
 */
interface ChatModalProps {
  otherUserId: string;
  visible: boolean;
  onClose: () => void;
  otherDisplayName?: string | null;
  canSendMessages?: boolean;
  onUpgradeClick?: () => void;
}

/**
 * parseTimestamp
 *
 * @description Parse various timestamp shapes into a number (ms since epoch).
 * Handles:
 *  - numeric milliseconds (>= 1e12)
 *  - numeric seconds (>= 1e9 && < 1e12) -> convert to ms
 *  - numeric strings
 *  - ISO and common SQL datetime strings (Date.parse)
 *
 * @param v any
 * @returns number | null (ms) or null if not parseable
 */
/**
 * parseTimestamp
 *
 * @description Parse various timestamp shapes into a number (ms since epoch).
 * Treats "naive" DB datetimes (like "2025-11-24 05:19:00.3800000" without timezone)
 * as UTC timestamps (per request). Also supports:
 *  - numeric milliseconds (>1e12)
 *  - numeric seconds (>1e9 && <1e12) -> convert to ms
 *  - numeric strings
 *  - ISO and common SQL datetime strings (with timezone) -> Date.parse
 *
 * @param v any
 * @returns number | null (ms) or null if not parseable
 */
function parseTimestamp(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  try {
    // If already a number (ms or seconds)
    if (typeof v === 'number' && !Number.isNaN(v)) {
      if (v > 1e12) return Math.floor(v); // milliseconds
      if (v > 1e9) return Math.floor(v * 1000); // seconds -> ms
      return null;
    }

    if (typeof v === 'string') {
      const s = v.trim();

      // Numeric-like string (seconds or ms)
      const num = Number(s);
      if (!Number.isNaN(num)) {
        if (num > 1e12) return Math.floor(num);
        if (num > 1e9) return Math.floor(num * 1000);
      }

      // ✅ FIXED: Handle naive datetime strings (no timezone) as LOCAL time consistently
      // This ensures optimistic and server messages parse the same way
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?[zZ]?$/);
      if (m) {
        const year = Number(m[1]);
        const month = Number(m[2]) - 1;
        const day = Number(m[3]);
        const hour = Number(m[4]);
        const minute = Number(m[5]);
        const second = Number(m[6]);
        const frac = m[7] ? (m[7].slice(0, 3)).padEnd(3, '0') : '000';
        const ms = Number(frac);

        // ✅ Always treat as LOCAL time (IST) - consistent for all messages
        return new Date(year, month, day, hour, minute, second, ms).getTime();
      }

      // Try Date.parse as fallback (handles full ISO strings with explicit timezone)
      const parsed = Date.parse(s);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * formatTimestamp
 *
 * @description Convert ms timestamp to a human readable string IN UTC.
 * Shows the exact time stored in database without timezone conversion.
 *
 * @param ms number | null
 */
function formatTimestamp(ms: number | null): string {
  if (!ms) return 'Sending...';
  try {
    const d = new Date(ms);
    // Use UTC methods to show exact DB time
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    const seconds = String(d.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return String(ms);
  }
}

/**
 * formatTimestampLocal
 *
 * @description Convert ms timestamp to LOCAL time string.
 * Used for SENT messages to show user's local time.
 *
 * @param ms number | null
 */
function formatTimestampLocal(ms: number | null): string {
  if (!ms) return 'Sending...';
  try {
    const d = new Date(ms);
    // Use LOCAL methods to show user's timezone
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return String(ms);
  }
}

/**
 * messageKey
 *
 * @description Deterministic signature for a message used for deduplication.
 * @param m ChatMessage
 */
function messageKey(m: ChatMessage) {
  return `${m.fromUserId}::${m.toUserId}::${String(m.message ?? '').trim()}`;
}

/**
 * normalizeIncomingMessage
 *
 * @description Convert various backend shapes into a ChatMessage with `message` property present.
 * Also normalizes `createdAt` to an ISO string where possible (so other code sees consistent shape).
 *
 * Supported message text keys: message, text, messageText, message_text, msg, body, preview, textContent, content
 *
 * @param raw any
 * @returns ChatMessage | null
 */
/**
 * normalizeIncomingMessage
 *
 * @description Convert various backend shapes into a ChatMessage with `message` property present.
 * Also normalizes `createdAt` into an ISO string when possible and marks whether the original
 * value was a "naive" DB datetime (no timezone). This flag is used to format the displayed time
 * in UTC (to match the stored value) instead of converting to local timezone.
 *
 * Supported message text keys: message, text, messageText, message_text, msg, body, preview, textContent, content
 *
 * @param raw any
 * @returns ChatMessage | null
 */
function normalizeIncomingMessage(raw: any): ChatMessage | null {
  if (!raw || typeof raw !== 'object') return null;

  const from =
    raw.fromUserId ??
    raw.from ??
    raw.senderId ??
    raw.from_user ??
    raw.sender ??
    raw.author ??
    null;
  const to =
    raw.toUserId ??
    raw.to ??
    raw.recipientId ??
    raw.to_user ??
    raw.recipient ??
    null;

  if (!from || !to) {
    return null;
  }

  const text =
    raw.message ??
    raw.text ??
    raw.messageText ??
    raw['message_text'] ??
    raw.msg ??
    raw.body ??
    raw.preview ??
    raw.textContent ??
    raw.content ??
    '';

  // ✅ FIXED: Keep the raw timestamp string as-is, don't convert to ISO
  const rawTime =
    raw.createdAt ?? raw.created_at ?? raw.time ?? raw.date ?? raw.ts ?? raw.timestamp ?? null;
  
  // Use the raw string directly, don't convert to ISO (which adds 'Z')
  const createdAtString = rawTime ? String(rawTime) : null;

  return {
    id: raw.id ?? raw._id ?? raw.clientMsgId ?? undefined,
    fromUserId: String(from),
    toUserId: String(to),
    message: String(text ?? ''),
    isRead: !!(raw.isRead || raw.read || raw.status === 'read'),
    createdAt: createdAtString,  // ✅ Raw string, no conversion
  } as ChatMessage;
}

/**
 * getMessageTime
 *
 * @description Return numeric timestamp (ms) for a ChatMessage.
 * Attempts parseTimestamp on message.createdAt. If unavailable, falls back to:
 *  - if id looks like optimistic temp id starting with 'temp:' we try to read the embedded ts
 *  - 0 if nothing else is available
 *
 * @param m ChatMessage
 */
function getMessageTime(m: ChatMessage): number {
  try {
    const created = (m as any).createdAt;
    const parsed = parseTimestamp(created);
    
    // ✅ Add logging for debugging
    if (String(m.id).startsWith('temp:')) {
      console.log('⏰ Parsing optimistic message:', {
        id: m.id,
        createdAt: created,
        parsed,
        formatted: created ? String(created).substring(0, 19) : 'N/A'
      });
    }
    
    if (parsed) return parsed;

    // Extract ms from optimistic temp id formed like 'temp:1732435241123:abc123'
    if (m.id && typeof m.id === 'string' && m.id.startsWith('temp:')) {
      const parts = m.id.split(':');
      if (parts.length >= 2) {
        const maybe = Number(parts[1]);
        if (!Number.isNaN(maybe) && maybe > 0) {
          console.log('⏰ Using timestamp from ID:', maybe);
          return maybe;
        }
      }
    }

    console.warn('⚠️ Could not parse timestamp, returning 0:', m.id);
    return 0;
  } catch (err) {
    console.error('❌ Error in getMessageTime:', err);
    return 0;
  }
}
/**
 * stableSortMessages
 *
 * @description Sort messages ascending by timestamp (older first). If timestamps equal use tie-breaker:
 *  - numeric id when possible
 *  - string id
 *  - fallback to insertion order deterministic via index
 *
 * @param arr ChatMessage[]
 * @returns ChatMessage[] sorted copy
 */
function stableSortMessages(arr: ChatMessage[]): ChatMessage[] {
  return arr
    .map((m, idx) => ({ m, idx, t: getMessageTime(m) }))
    .sort((a, b) => {
      if (a.t !== b.t) return a.t - b.t;
      // try numeric id
      const aid = a.m.id;
      const bid = b.m.id;
      const an = typeof aid === 'number' ? aid : Number(String(aid || ''));
      const bn = typeof bid === 'number' ? bid : Number(String(bid || ''));
      if (!Number.isNaN(an) && !Number.isNaN(bn) && an !== bn) return an - bn;
      if (String(aid) !== String(bid)) return String(aid).localeCompare(String(bid));
      return a.idx - b.idx;
    })
    .map((it) => it.m);
}

/**
 * ChatModal
 *
 * @description Chat modal that shows conversation with otherUserId.
 * - Handles optimistic messages, SSE-driven updates without duplicates.
 * - Auto-scrolls to bottom when new messages appear.
 * - Emits 'sider-chat-loaded' when history is rendered (handshake for safe mark-read).
 */
export default function ChatModal({
  otherUserId,
  visible,
  onClose,
  otherDisplayName: propDisplayName,
  canSendMessages = true,
  onUpgradeClick
}: ChatModalProps) {
  const { messages: sseMessagesRaw, unreadCount: sseUnreadCount, sendMessage, markRead, connected } =
    useChat(undefined, visible);
  const storeUser = useAuthStore((s) => s.user);

  const [input, setInput] = useState('');
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [otherDisplayName, setOtherDisplayName] = useState<string | null>(propDisplayName ?? null);
  const [localUnreadCount, setLocalUnreadCount] = useState<number | null>(null);

  const currentUserId = useMemo(() => {
    try {
      if (storeUser && storeUser.userId) return String(storeUser.userId);
      const stored = localStorage.getItem('userData');
      if (!stored) return '';
      const user = JSON.parse(stored);
      return String(user?.userId ?? '');
    } catch {
      return '';
    }
  }, [storeUser]);

  const seenRef = useRef<Set<string>>(new Set());
  const tempMapRef = useRef<Record<string, { text: string; ts: number }>>({});

  const listRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);

  // Track modal view when opened
  useEffect(() => {
    if (visible) {
      trackModalView('Chat Modal');
    }
  }, [visible]);

  // Use prop displayName immediately if provided (normalize empty/whitespace strings)
  useEffect(() => {
    /**
     * Normalize propDisplayName so empty strings or whitespace do not override
     * the header. We want a meaningful non-empty value only.
     */
    const normalizedProp =
      typeof propDisplayName === 'string' && propDisplayName.trim() ? propDisplayName.trim() : null;
    setOtherDisplayName(normalizedProp);
  }, [propDisplayName]);

  // Read sessionStorage intent and listen for in-time open events
  useEffect(() => {
    if (!otherUserId || !visible) return;

    try {
      const raw = sessionStorage.getItem('sider.openChat');
      if (raw) {
        try {
          const obj = JSON.parse(raw);
          if (obj && String(obj.partnerId) === String(otherUserId)) {
            if (!obj.ts || Number(obj.ts) + 60_000 > Date.now()) {
              if (obj.displayName) setOtherDisplayName(String(obj.displayName));
            }
          }
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore storage errors
    }

    function onOpenEvent(ev: Event) {
      try {
        const detail = (ev as CustomEvent).detail;
        if (!detail) return;
        const pid = String(detail.partnerId ?? '');
        if (pid !== otherUserId) return;
        const name = detail.displayName ?? null;
        if (name) {
          setOtherDisplayName(String(name));
          try {
            sessionStorage.setItem(
              'sider.openChat',
              JSON.stringify({ partnerId: pid, displayName: name, ts: Date.now() })
            );
          } catch {}
        }
      } catch {
        // ignore
      }
    }

    window.addEventListener('sider-open-chat', onOpenEvent as EventListener);
    return () => {
      window.removeEventListener('sider-open-chat', onOpenEvent as EventListener);
    };
  }, [otherUserId, visible]);

  /**
   * fetchOtherDisplayName
   *
   * @description When the ChatModal is visible and we don't already have a display
   * name from props/session, try resolving the partner's name from the backend using
   * the existing userApi.fetchUserProfile helper. This improves the header by ensuring
   * a friendly name is shown even if the opening component didn't pass it.
   *
   * Notes:
   * - Uses dynamic import to avoid bundling extra code when ChatModal isn't used.
   * - Sets otherDisplayName only when a useful value is found.
   */
  useEffect(() => {
    let mounted = true;
    if (!visible || !otherUserId) return;
    if (otherDisplayName) return; // already resolved

    (async () => {
      try {
        const { fetchUserProfile } = await import('@/utils/userApi');
        const profile = await fetchUserProfile(String(otherUserId));
        if (!mounted) return;
        if (!profile) return;

        // Build a friendly display name from best available fields
        // NOTE: avoid mixing `??` and `||` without parentheses to satisfy the parser.
        const display = (() => {
          // 1) Prefer explicit displayName
          if (profile.displayName) return profile.displayName;
          // 2) Fallback to concatenated first + last name
          const full = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
          if (full) return full;
          // 3) Try raw nested fields (use nullish coalescing safely)
          if (profile.raw) {
            const rawName = profile.raw.displayName ?? profile.raw.name ?? null;
            if (rawName) return rawName;
          }
          // 4) Nothing found
          return null;
        })();

        if (display) setOtherDisplayName(String(display));
      } catch {
        // Best-effort: ignore failures, leave otherDisplayName as-is (will fall back to id)
      }
    })();

    return () => {
      mounted = false;
    };
  }, [visible, otherUserId, otherDisplayName]);

  /**
   * tryFetchChatHistory
   * @description Attempt to fetch history using several common endpoints.
   */
  async function tryFetchChatHistory(myId: string, theirId: string): Promise<ChatMessage[]> {
    const endpoints = [
      `/api/chat/history/${encodeURIComponent(myId)}/${encodeURIComponent(theirId)}`,
      `/api/chat/history?userId=${encodeURIComponent(myId)}&with=${encodeURIComponent(theirId)}`,
      `/api/messages/history/${encodeURIComponent(myId)}/${encodeURIComponent(theirId)}`,
      `/api/messages?userId=${encodeURIComponent(myId)}&partnerId=${encodeURIComponent(theirId)}`,
      `/api/chat/messages/${encodeURIComponent(myId)}/${encodeURIComponent(theirId)}`,
      `/api/chat/history/with/${encodeURIComponent(theirId)}`,
      `/api/chat/history/${encodeURIComponent(theirId)}`,
      `/api/chat/messages?partnerId=${encodeURIComponent(theirId)}`,
      `/api/messages?partnerId=${encodeURIComponent(theirId)}`,
      `/chat/history/${encodeURIComponent(myId)}/${encodeURIComponent(theirId)}`,
      `/messages/history/${encodeURIComponent(myId)}/${encodeURIComponent(theirId)}`
    ];

    for (const p of endpoints) {
      try {
        const res = await fetch(p);
        if (!res.ok) continue;
        let json: any = null;
        try {
          json = await res.json();
        } catch {
          continue;
        }
        const payload = json?.messages ?? json?.data ?? json ?? null;
        const arr = deriveMessagesFromPayload(payload);
        if (arr && arr.length > 0) return arr;
      } catch {
        // continue
      }
    }
    return [];
  }




  
  /**
   * deriveMessagesFromPayload
   * Convert flexible payload shapes into ChatMessage[] using normalizer.
   */
  function deriveMessagesFromPayload(payload: any): ChatMessage[] {
    try {
      if (!payload) return [];
      if (Array.isArray(payload)) {
        return payload
          .map((m) => normalizeIncomingMessage(m))
          .filter(Boolean) as ChatMessage[];
      }
      const candidateKeys = ['messages', 'data', 'items', 'rows', 'list'];
      for (const k of candidateKeys) {
        if (Array.isArray(payload[k])) return deriveMessagesFromPayload(payload[k]);
      }
      function findArray(obj: any): any[] | null {
        if (!obj || typeof obj !== 'object') return null;
        if (Array.isArray(obj)) {
          if (obj.length > 0) return obj;
          return null;
        }
        for (const key of Object.keys(obj)) {
          const found = findArray(obj[key]);
          if (found) return found;
        }
        return null;
      }
      const found = findArray(payload);
      if (found) return deriveMessagesFromPayload(found);
      return [];
    } catch {
      return [];
    }
  }

  // Fetch initial history when modal opens
  useEffect(() => {
    if (!visible || !otherUserId) return;
    let mounted = true;

    (async () => {
      setLoadingHistory(true);
      try {
        seenRef.current = new Set();
        tempMapRef.current = {};

        const msgs = await tryFetchChatHistory(currentUserId, otherUserId);

        let finalMsgs: ChatMessage[] = msgs;
        if ((!finalMsgs || finalMsgs.length === 0) && Array.isArray(sseMessagesRaw) && sseMessagesRaw.length > 0) {
          const fromSSE = (sseMessagesRaw as any[])
            .map(normalizeIncomingMessage)
            .filter(Boolean)
            .filter(
              (m) =>
                (m!.fromUserId === otherUserId && m!.toUserId === currentUserId) ||
                (m!.fromUserId === currentUserId && m!.toUserId === otherUserId)
            ) as ChatMessage[];
          if (fromSSE.length > 0) finalMsgs = fromSSE;
        }

        // Deduplicate and sort using stableSortMessages
        const out: ChatMessage[] = [];
        for (const m of finalMsgs) {
          if (!m) continue;
          const k = messageKey(m);
          if (!seenRef.current.has(k)) {
            seenRef.current.add(k);
            out.push(m);
          }
        }

        const sorted = stableSortMessages(out);
        if (mounted) setHistory(sorted);

        if (mounted) {
          const unreadFromServer = sseUnreadCount ?? null;
          if (typeof unreadFromServer === 'number') {
            setLocalUnreadCount(unreadFromServer);
          } else {
            const unreadCalc = sorted.reduce((acc, it) => {
              try {
                return acc + ((String(it.toUserId) === String(currentUserId) && (it.isRead === false || it.isRead === undefined)) ? 1 : 0);
              } catch {
                return acc;
              }
            }, 0);
            setLocalUnreadCount(unreadCalc);
          }
        }
      } catch {
        if (mounted) setHistory([]);
      } finally {
        if (mounted) setLoadingHistory(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, otherUserId, currentUserId]);

  // Merge SSE messages into history using normalization and stable sorting
  useEffect(() => {
    if (!visible) return;
    if (!Array.isArray(sseMessagesRaw) || sseMessagesRaw.length === 0) return;
    if (!currentUserId) return;

    const normalized = (sseMessagesRaw as any[]).map(normalizeIncomingMessage).filter(Boolean) as ChatMessage[];

    const relevant = normalized.filter(
      (m) =>
        (m.fromUserId === otherUserId && m.toUserId === currentUserId) ||
        (m.fromUserId === currentUserId && m.toUserId === otherUserId)
    );
    if (relevant.length === 0) return;

    setHistory((prev) => {
      const out = [...prev];
      for (const m of relevant) {
        if (!m) continue;
        const k = messageKey(m);
        if (seenRef.current.has(k)) {
          // Replace optimistic message that matches this server message if possible
          if (m.id) {
            const matchIndex = out.findIndex((hm) => {
              return String(hm.id).startsWith('temp:') && hm.fromUserId === m.fromUserId && String(hm.message).trim() === String(m.message).trim();
            });
            if (matchIndex !== -1) {
              out[matchIndex] = m;
              seenRef.current.add(k);
              continue;
            }
          }
          continue;
        }

        seenRef.current.add(k);

        if (m.id) {
          const matchIndex = out.findIndex((hm) => {
            return String(hm.id).startsWith('temp:') && hm.fromUserId === m.fromUserId && String(hm.message).trim() === String(m.message).trim();
          });
          if (matchIndex !== -1) {
            out[matchIndex] = m;
            continue;
          }
        }

        out.push(m);
      }

      const sorted = stableSortMessages(out);

      try {
        const unreadCalc = sorted.reduce((acc, it) => {
          try {
            return acc + ((String(it.toUserId) === String(currentUserId) && (it.isRead === false || it.isRead === undefined)) ? 1 : 0);
          } catch {
            return acc;
          }
        }, 0);
        setLocalUnreadCount(unreadCalc);
      } catch {
        // ignore
      }

      return sorted;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseMessagesRaw, otherUserId, currentUserId, visible]);

  // Emit 'sider-chat-loaded' and mark-read behavior (deferred) - handshake
  const markReadAfterRenderRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (!visible) return;
    if (loadingHistory) return;
    if (!otherUserId) return;
    if (!currentUserId) return;

    if (markReadAfterRenderRef.current[otherUserId]) return;

    const t = setTimeout(async () => {
      try {
        try {
          window.dispatchEvent(new CustomEvent('sider-chat-loaded', { detail: { partnerId: otherUserId, ts: Date.now() } }));
        } catch (e) {}
        await new Promise((r) => setTimeout(r, 120));
        await markRead?.(otherUserId);
      } catch {
        // ignore
      }

      setLocalUnreadCount(0);
      markReadAfterRenderRef.current[otherUserId] = true;
    }, 450);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingHistory, visible, otherUserId, currentUserId]);

  /**
   * handleSend
   * Insert optimistic message with a temp id and a createdAt that encodes ms for stable sorting.
   */
async function handleSend() {
  if (!input || input.trim() === '') return;
  const trimmed = input.trim();
  
  // Get current LOCAL time (what user sees on their clock)
  const now = new Date();
  const ts = now.getTime(); // Milliseconds since epoch (universal)
  
  // Create tempId with actual milliseconds (universal timestamp)
  const tempId = `temp:${ts}:${Math.random().toString(36).slice(2, 8)}`;
  
  // ✅ FIXED: Create datetime string using LOCAL time (matches user's clock)
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const ms = String(now.getMilliseconds()).padStart(3, '0');
  
  // Create datetime string in LOCAL timezone (what user sees)
  const localDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;

  console.log('📤 Sending message:', {
    localTime: now.toLocaleString(),
    localDateTime,
    timestamp: ts
  });

  const optimisticMessage: ChatMessage = {
    id: tempId as any,
    fromUserId: currentUserId ?? '',
    toUserId: otherUserId,
    message: trimmed,
    createdAt: localDateTime, // ✅ Use LOCAL time string (matches what user sees)
    isRead: false
  };

  tempMapRef.current[tempId] = { text: trimmed, ts };

  setHistory((prev) => {
    const out = [...prev, optimisticMessage];
    seenRef.current.add(messageKey(optimisticMessage));
    return stableSortMessages(out);
  });

  setInput('');

  try {
    const result = await sendMessage(otherUserId, trimmed);
    if (!result || !result.success) {
      setHistory((prev) => prev.filter((m) => String(m.id) !== tempId));
      delete tempMapRef.current[tempId];
      alert(result?.error || 'Failed to send message');
    }
  } catch (err) {
    setHistory((prev) => prev.filter((m) => String(m.id) !== tempId));
    delete tempMapRef.current[tempId];
    alert('Failed to send message');
  }
}
  function scrollToBottom(smooth = true) {
    try {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
        return;
      }
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => scrollToBottom(true), 120);
    return () => clearTimeout(t);
  }, [history.length, visible]);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => scrollToBottom(false), 120);
      return () => clearTimeout(t);
    }
    return;
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none">
      <div className="w-full sm:w-96 max-h-[80vh] bg-white rounded-lg shadow-xl overflow-hidden pointer-events-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center space-x-3">
            <MessageCircle className="w-5 h-5 text-white" />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Chat</div>
              <div className="text-xs opacity-90">with {(() => {
                /**
                 * Compute header name with a stable priority:
                 * 1) propDisplayName when it's a non-empty trimmed string
                 * 2) otherDisplayName when it's a non-empty trimmed string (resolved from session/fetch)
                 * 3) fallback to otherUserId
                 *
                 * This inline normalization ensures the header never shows an empty string.
                 */
                const p = typeof propDisplayName === 'string' && propDisplayName.trim() ? propDisplayName.trim() : null;
                const o = typeof otherDisplayName === 'string' && otherDisplayName.trim() ? otherDisplayName.trim() : null;
                return p ?? o ?? otherUserId;
              })()}</div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="text-xs opacity-90 mr-2">Unread: {localUnreadCount ?? sseUnreadCount ?? 0}</div>
            <Button variant="ghost" className="bg-transparent text-white/90" onClick={onClose} title="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 p-3 overflow-y-auto space-y-3 bg-slate-50 pb-20" aria-live="polite" aria-atomic="false">
          {loadingHistory ? (
            <div className="text-center text-xs text-slate-500 py-6">Loading chat...</div>
          ) : history.length === 0 ? (
            <div className="text-center text-xs text-slate-400 py-6">No messages yet. Say hi 👋</div>
          ) : (
            history.map((m, idx) => {
  const mine = String(m.fromUserId ?? '') === String(currentUserId ?? '');
  const isRead = !!m.isRead;
  
  // ✅ SIMPLEST FIX: Just use the raw createdAt string from DB
  const timestampText = m.createdAt 
    ? String(m.createdAt).replace('T', ' ').replace('Z', '').substring(0, 19)
    : 'Sending...';

  return (
    <div 
      key={`${idx}::${String(m.id ?? '')}`} 
      className={`flex ${mine ? 'justify-end' : 'justify-start'}`} 
      ref={idx === history.length - 1 ? lastMessageRef : undefined}
    >
      <div className={`relative max-w-[80%] px-3 py-2 rounded-lg ${mine ? 'bg-purple-600 text-white' : 'bg-pink-100 text-slate-800 border border-pink-200'}`}>
        <div className="text-sm break-words whitespace-pre-wrap">{m.message}</div>

        {/* ✅ Display raw timestamp from DB - no conversions! */}
        <div 
          className={`text-xs mt-1 ${mine ? 'text-white/80 text-right' : 'text-slate-600 text-left'}`} 
          aria-label={`${mine ? 'Sent at' : 'Received at'} ${timestampText}`}
        >
          {timestampText}
        </div>

        {mine && (
          <div className="absolute bottom-1 right-1 flex items-center space-x-0.5" role="img" aria-label={isRead ? 'Read' : 'Sent'}>
            {!isRead ? (
              <div className="flex items-center text-white/70" aria-hidden>
                <Check className="w-3 h-3" />
              </div>
            ) : (
              <div className="flex items-center text-emerald-400" aria-hidden>
                <Check className="w-3 h-3" />
                <Check className="w-3 h-3 -ml-1" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
})
          )}
        </div>

        {/* Input */}
        <div className="px-3 py-2 border-t bg-white">
          {!canSendMessages ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <div className="text-amber-800 font-medium mb-2">
                Chat available for Premium Users only
              </div>
              <p className="text-sm text-amber-600 mb-3">
                You can read messages, but sending is restricted to Premium members.
              </p>
              <Button 
                onClick={() => {
                  if (onUpgradeClick) {
                    onUpgradeClick();
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
              >
                Upgrade to Premium
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={connected ? 'Type a message...' : 'Connecting...'}
                rows={3}
                className="flex-1 px-3 py-2 rounded-md border focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                aria-label="Type a message"
              />
              <Button onClick={handleSend} className="bg-blue-600 text-white">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}